import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Mail, Menu, MessageCircle, Phone, Search, X } from "lucide-react";
import { textLang } from "@triple-h/shared";
import { SearchDialog } from "./SearchDialog";
import { Brand } from "./Brand";
import { useDialog } from "../hooks/useDialog";
import { useSite } from "../hooks/useSite";
import { fallbackSite, phoneHref, viberHref } from "../lib/site";

type NavItem = { to: string; label: string; kind?: "all" | "sale" | "page" };

const primaryNav: NavItem[] = [
  { to: "/properties", label: "Properties", kind: "all" },
  { to: "/properties?type=sale", label: "For Sale", kind: "sale" },
  { to: "/gallery", label: "Gallery", kind: "page" },
  { to: "/about", label: "About", kind: "page" },
  { to: "/contact", label: "Contact", kind: "page" }
];

function useActiveNav() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const type = params.get("type");

  return (item: NavItem) => {
    if (item.kind === "page") return location.pathname === item.to;
    const inProperties = location.pathname === "/properties" || location.pathname.startsWith("/property/");
    if (!inProperties) return false;
    if (item.kind === "sale") return location.pathname === "/properties" && type === "sale";
    return type !== "sale";
  };
}

export function Header() {
  const { data: site = fallbackSite } = useSite();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const location = useLocation();
  const isActive = useActiveNav();
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [menuTop, setMenuTop] = useState<number | null>(null);

  // The sticky header sits below the top line until the page is scrolled, so
  // its bottom edge is not always `--header-height`. Measure the real edge so
  // the overlay never covers the header at tablet widths.
  useLayoutEffect(() => {
    if (!open) {
      setMenuTop(null);
      return;
    }
    const update = () => {
      const rect = headerRef.current?.getBoundingClientRect();
      if (rect) setMenuTop(Math.max(0, Math.round(rect.bottom)));
    };
    update();
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, [open]);

  const closeMenu = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => toggleRef.current?.focus({ preventScroll: true }));
    }
  }, []);

  const closeMenuFromDialog = useCallback(() => closeMenu(true), [closeMenu]);
  const menuRef = useDialog(open, closeMenuFromDialog);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  return (
    <>
      <div className="header-topline">
        <div className="container topline-inner">
          <span className="topline-tagline" lang={textLang(site.tagline)}>{site.tagline || "Real Estate with Integrity"}</span>
          <span className="topline-burmese" lang="my">အိမ်၊ ခြံ၊ မြေနှင့် စီးပွားရေးအဆောက်အအုံများ</span>
          <div className="topline-actions">
            <a href={phoneHref(site.publicPhone)}><Phone aria-hidden="true" /><span>{site.publicPhone}</span></a>
            <a href={`mailto:${site.email}`}><Mail aria-hidden="true" /><span>{site.email}</span></a>
          </div>
        </div>
      </div>

      <header ref={headerRef} className={`site-header ${open ? "menu-open" : ""}`}>
        <div className="container header-inner">
          <Brand />
          <nav className="desktop-nav" aria-label="Primary navigation">
            {primaryNav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={isActive(item) ? "is-active" : ""}
                aria-current={isActive(item) ? "page" : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="header-actions">
            <button type="button" className="header-search" aria-label="Search properties" aria-haspopup="dialog" onClick={() => { setOpen(false); setSearchOpen(true); }}><Search aria-hidden="true" /><span>Search</span></button>
            <a className="header-phone" href={phoneHref(site.publicPhone)} aria-label={`Call Triple H at ${site.publicPhone}`}>
              <Phone aria-hidden="true" />
              <span>{site.publicPhone}</span>
            </a>
            <Link className="btn btn-primary btn-small header-enquire" to="/contact">
              Enquire <ArrowRight aria-hidden="true" />
            </Link>
            <button
              className="menu-toggle"
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls={menuId}
              onClick={() => setOpen((value) => !value)}
              ref={toggleRef}
            >
              {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
            </button>
          </div>
        </div>

      </header>

      <SearchDialog open={searchOpen} onClose={closeSearch} />
      {createPortal(
        <div
          className={`mobile-panel-layer ${open ? "is-open" : ""}`}
          aria-hidden={!open}
          style={menuTop === null ? undefined : ({ "--mobile-menu-top": `${menuTop}px` } as CSSProperties)}
        >
          <button
            className={`menu-backdrop ${open ? "is-open" : ""}`}
            type="button"
            aria-label="Close menu"
            aria-hidden={!open}
            tabIndex={-1}
            onClick={() => closeMenu(true)}
          />
          <div
            id={menuId}
            className={`mobile-panel ${open ? "is-open" : ""}`}
            ref={menuRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            aria-hidden={!open}
            inert={!open}
            tabIndex={-1}
          >
            <div className="mobile-panel-head">
              <span className="eyebrow">Menu</span>
              <button type="button" className="icon-button" aria-label="Close menu" onClick={() => closeMenu(true)}>
                <X aria-hidden="true" />
              </button>
            </div>
            <nav className="mobile-nav" aria-label="Mobile navigation">
              {primaryNav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={isActive(item) ? "is-active" : ""}
                  aria-current={isActive(item) ? "page" : undefined}
                  onClick={() => closeMenu(false)}
                >
                  <span>{item.label}</span>
                  <ArrowRight aria-hidden="true" />
                </Link>
              ))}
            </nav>
            <div className="mobile-contact">
              <a href={phoneHref(site.publicPhone)}>
                <Phone aria-hidden="true" />
                <span><small>Call us</small><strong>{site.publicPhone}</strong></span>
              </a>
              <a href={`mailto:${site.email}`}>
                <Mail aria-hidden="true" />
                <span><small>Email</small><strong>{site.email}</strong></span>
              </a>
              {site.viberUrl && (<a href={viberHref(site)}>
                <MessageCircle aria-hidden="true" />
                <span><small>Viber</small><strong>Message our team</strong></span>
              </a>)}
              {site.messengerUrl && (
                <a href={site.messengerUrl} target="_blank" rel="noreferrer">
                  <MessageCircle aria-hidden="true" />
                  <span><small>Messenger</small><strong>Open Messenger</strong></span>
                </a>
              )}
            </div>
            <p className="mobile-panel-note" lang="my">အိမ်၊ ခြံ၊ မြေနှင့် စီးပွားရေးအဆောက်အအုံများအတွက် ကျွန်ုပ်တို့ကို ဆက်သွယ်နိုင်ပါသည်။</p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
